import React from 'react';
import { useStore } from 'effector-react';
import { useImmer } from 'use-immer';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  type HSIconImage,
  type HSIconSeqFrame,
  type HSIconVideo,
  HSPosType,
} from '@types';
import { sendWarning } from '@components/Noti';
import Input from '@components/Input';
import Button from '@components/Button';
import TextLink from '@components/TextLink';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import WidgetRow from '@components/PlayerWidgets/WidgetRow';
import { reAddEditingHotspot } from '../../actions';

type Icon = HSIconImage | HSIconSeqFrame | HSIconVideo;
type Keys = 'hFov' | 'yaw' | 'pitch' | 'roll';

const valsRange: Record<Keys, [number, number]> = {
  hFov: [1, 179],
  yaw: [-180, 180],
  pitch: [-90, 90],
  roll: [-999999, 999999],
};

interface InputProps {
  value: string;
  onChange: (key: Keys, value: string) => void;
  onClear: (key: Keys) => void;
  propKey: Keys;
}

function ValInput({ value, propKey, onChange, onClear }: InputProps) {
  return (
    <Input
      className="mb-20 h-30 w-full"
      scheme="dark"
      value={value}
      placeholder="请输入坐标值"
      onClear={() => onClear(propKey)}
      onChange={(e) => onChange(propKey, e.target.value)}
    />
  );
}

function CoordControl() {
  const { hotspot } = useStore($editingHotspot)!;
  const icon = hotspot.icon as Icon;

  const [vals, setVals] = useImmer<Record<Keys, string>>({
    hFov: String(icon.hFov ?? ''),
    yaw: String(icon.yaw ?? ''),
    pitch: String(icon.pitch ?? ''),
    roll: String(icon.roll ?? ''),
  });

  const disabled = !(vals.hFov && vals.yaw && vals.pitch && vals.roll);

  const valCheck = (): boolean => {
    return Object.entries(vals).reduce((acc, cur: [Keys, string]) => {
      const [k, v] = cur;
      if (isNumeric(v)) {
        const num = Number(v);
        const inRange = num >= valsRange[k][0] && num <= valsRange[k][1];
        return inRange && acc;
      }
      return false;
    }, true);
  };

  const handleChange = (key: Keys, value: string) => {
    setVals((draft) => {
      draft[key] = value;
    });
  };

  const handleClear = (key: Keys) => {
    setVals((draft) => {
      draft[key] = '';
    });
  };

  const handleBtnClick = () => {
    if (!valCheck()) {
      sendWarning('输入的坐标值异常，内容无法正确对齐，请输入正确的坐标值');
      return;
    }
    $editingHotspotApi.update((draft) => {
      const iconData = draft.icon as Icon;
      iconData.positionType = HSPosType.adv;
      iconData.hFov = Number(vals.hFov);
      iconData.yaw = Number(vals.yaw);
      iconData.pitch = Number(vals.pitch);
      iconData.roll = Number(vals.roll);
    });
    reAddEditingHotspot();
  };

  return (
    <>
      <WidgetRow>
        <div className="mb-10">水平视场（HFOV）：</div>
        <ValInput
          value={vals.hFov}
          propKey="hFov"
          onChange={handleChange}
          onClear={handleClear}
        />
        <div className="mb-10">X轴（Yaw）：</div>
        <ValInput
          value={vals.yaw}
          propKey="yaw"
          onChange={handleChange}
          onClear={handleClear}
        />
        <div className="mb-10">Y轴（Pitch）：</div>
        <ValInput
          value={vals.pitch}
          propKey="pitch"
          onChange={handleChange}
          onClear={handleClear}
        />
        <div className="mb-10">Z轴（Roll）：</div>
        <ValInput
          value={vals.roll}
          propKey="roll"
          onChange={handleChange}
          onClear={handleClear}
        />
        <Button disabled={disabled} className="w-full" onClick={handleBtnClick}>
          对齐
        </Button>
      </WidgetRow>
      <div className="mt-10 flex justify-between">
        <TextLink href="https://bbs2.720yun.com/article?id=392" target="_blank">
          使用教程
        </TextLink>
        <TextLink
          onClick={() => setVals({ hFov: '', yaw: '', pitch: '', roll: '' })}
        >
          清空
        </TextLink>
      </div>
    </>
  );
}

export default CoordControl;
